import { Skull } from "lucide-react";
import { classChibi } from "@/lib/classChibi";

const CLASS_ACCENT = {
  Warrior: "#8A0303",
  Guardian: "#4A7BC7",
  Rogue: "#5D2F5F",
  Wizard: "#E55A00",
  Bard: "#B89C3A",
};

// ─── Appearance palettes (shared with CharacterCreator) ─────────────
export const SKIN_TONES = {
  pale:   "#E8CBA8",
  olive:  "#B08A5E",
  dark:   "#6B4A2A",
  ashen:  "#8A8A8A",
};
export const HAIR_COLORS = {
  raven:   "#1a1a1a",
  ash:     "#6B6B6B",
  ember:   "#8A2A02",
  gold:    "#B89C3A",
  bone:    "#E2DFD8",
};
export const EYE_COLORS = {
  ember:   "#E55A00",
  amber:   "#E5C34A",
  moss:    "#6E8B3D",
  ice:     "#4A7BC7",
  violet:  "#8A2BE2",
};
export const CLOAK_COLORS = {
  blood:   "#5A0A0A",
  slate:   "#1A2333",
  moss:    "#2F3D1E",
  royal:   "#3A1F5E",
  ash:     "#2B2B2B",
};
export const HAIR_STYLES = ["short", "long", "hood", "bald", "mohawk"];
export const MARKINGS   = ["none", "scar", "warpaint", "brand"];

/**
 * Renders a small stylised pixel-portrait. If `appearance` is provided it
 * overrides the default class palette. Backwards compatible: fresh users
 * without an appearance object still get the old class-tinted skull look.
 */
export default function Portrait({
  name = "",
  cls = "Warrior",
  cloak_color,
  size = 40,
  active_title,
  appearance,
}) {
  const seed = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
  const legacyScar = !appearance && seed % 3 === 0;

  const cloak = appearance?.cloak
    ? (CLOAK_COLORS[appearance.cloak] || appearance.cloak)
    : (cloak_color || CLASS_ACCENT[cls] || "#333");
  const skin = SKIN_TONES[appearance?.skin] || "#B08A5E";
  const hairColor = HAIR_COLORS[appearance?.hair_color] || "#1a1a1a";
  const eyeColor = EYE_COLORS[appearance?.eyes] || CLASS_ACCENT[cls] || "#E55A00";
  const hair = appearance?.hair || null;
  const marking = appearance?.marking || (legacyScar ? "scar" : "none");
  const borderColor = active_title ? "#B89C3A" : "#333";
  const eye = Math.max(2, Math.floor(size * 0.08));

  // ─── Chibi headshot (highest priority) ─────────────────────────────
  // If we know the class + gender, render the actual character art cropped
  // to the head/torso. Falls through to CSS layers otherwise.
  const chibi = classChibi(cls, appearance?.gender, appearance?.class_image);
  if (chibi) {
    return (
      <div
        data-testid={`portrait-${name}`}
        className="relative shrink-0 overflow-hidden"
        style={{
          width: size,
          height: size,
          background: "#0a0a0a",
          border: `2px solid ${borderColor}`,
        }}
      >
        <img
          src={chibi}
          alt={`${cls} portrait`}
          draggable={false}
          className="pointer-events-none select-none absolute"
          style={{
            // Zoom + shift up so the head fills the square.
            top: `-${Math.round(size * 0.05)}px`,
            left: "50%",
            width: `${Math.round(size * 1.6)}px`,
            height: `${Math.round(size * 1.6)}px`,
            transform: "translateX(-50%)",
            objectFit: "cover",
            objectPosition: "50% 12%",
          }}
        />
        {active_title && (
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{ boxShadow: "inset 0 0 6px rgba(184,156,58,0.5)" }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      data-testid={`portrait-${name}`}
      className="relative flex items-center justify-center shrink-0 overflow-hidden"
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg, ${cloak} 0%, #0a0a0a 140%)`,
        border: `2px solid ${borderColor}`,
      }}
    >
      {appearance ? (
        <>
          {/* Face */}
          <div
            className="absolute"
            style={{
              left: size * 0.20, top: size * 0.22,
              width: size * 0.60, height: size * 0.56,
              background: skin,
              boxShadow: `inset 0 -${size * 0.12}px 0 rgba(0,0,0,0.25)`,
            }}
          />
          {/* Hair */}
          {hair && hair !== "bald" && (
            <>
              {(hair === "short" || hair === "long" || hair === "mohawk") && (
                <div
                  className="absolute"
                  style={{
                    left: size * 0.20, top: size * 0.14,
                    width: size * 0.60, height: hair === "mohawk" ? size * 0.24 : size * 0.14,
                    background: hairColor,
                    clipPath: hair === "mohawk" ? "polygon(35% 0, 65% 0, 65% 100%, 35% 100%)" : "none",
                  }}
                />
              )}
              {hair === "long" && (
                <div
                  className="absolute"
                  style={{
                    left: size * 0.12, top: size * 0.28,
                    width: size * 0.10, height: size * 0.48,
                    background: hairColor,
                  }}
                />
              )}
              {hair === "long" && (
                <div
                  className="absolute"
                  style={{
                    left: size * 0.78, top: size * 0.28,
                    width: size * 0.10, height: size * 0.48,
                    background: hairColor,
                  }}
                />
              )}
              {hair === "hood" && (
                <div
                  className="absolute"
                  style={{
                    left: size * 0.08, top: size * 0.06,
                    width: size * 0.84, height: size * 0.42,
                    background: cloak,
                    filter: "brightness(0.7)",
                    clipPath: "polygon(0 100%, 15% 30%, 50% 0, 85% 30%, 100% 100%)",
                  }}
                />
              )}
            </>
          )}
          {/* Eyes */}
          <div
            className="absolute"
            style={{
              top: size * 0.42, left: size * 0.30,
              width: eye, height: eye,
              background: eyeColor,
              boxShadow: `0 0 ${eye}px ${eyeColor}`,
            }}
          />
          <div
            className="absolute"
            style={{
              top: size * 0.42, right: size * 0.30,
              width: eye, height: eye,
              background: eyeColor,
              boxShadow: `0 0 ${eye}px ${eyeColor}`,
            }}
          />
          {/* Marking */}
          {marking === "scar" && (
            <div
              className="absolute"
              style={{
                top: size * 0.32, left: size * 0.60,
                width: 2, height: size * 0.32,
                background: "#8A0303",
                transform: "rotate(15deg)",
                opacity: 0.85,
              }}
            />
          )}
          {marking === "warpaint" && (
            <div
              className="absolute"
              style={{
                top: size * 0.55, left: size * 0.30,
                width: size * 0.40, height: 2,
                background: "#8A0303",
                opacity: 0.9,
              }}
            />
          )}
          {marking === "brand" && (
            <div
              className="absolute"
              style={{
                top: size * 0.28, left: size * 0.45,
                width: size * 0.14, height: size * 0.14,
                borderRadius: "50%",
                border: "1px solid #E55A00",
                background: "transparent",
                boxShadow: "0 0 4px #E55A00",
              }}
            />
          )}
        </>
      ) : (
        <>
          {/* Legacy fallback for characters without appearance */}
          <Skull
            style={{ width: size * 0.7, height: size * 0.7, color: "#050505", opacity: 0.55 }}
            strokeWidth={1.2}
          />
          <div
            className="absolute"
            style={{
              top: size * 0.35, left: size * 0.30,
              width: eye, height: eye,
              background: eyeColor,
              boxShadow: `0 0 ${eye}px ${eyeColor}`,
            }}
          />
          <div
            className="absolute"
            style={{
              top: size * 0.35, right: size * 0.30,
              width: eye, height: eye,
              background: eyeColor,
              boxShadow: `0 0 ${eye}px ${eyeColor}`,
            }}
          />
          {legacyScar && (
            <div
              className="absolute"
              style={{
                top: size * 0.15, left: size * 0.55,
                width: 2, height: size * 0.35,
                background: "#8A0303",
                transform: "rotate(15deg)",
                opacity: 0.7,
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
